import { demoOrders, demoProducts, demoTrustCenter } from '../data/demo'
import type {
  ConfigurationValidation,
  CustomerOrder,
  InquiryPayload,
  InquiryResult,
  Product,
  ProductConfiguration,
  ProductSpecification,
  PurchaseMode,
  ServiceAreaResult,
  SurveyBookingPayload,
  TrustCenter,
} from '../types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'
const DEMO_FALLBACK = import.meta.env.VITE_ENABLE_DEMO_FALLBACK !== 'false'
const DEMO_MODE_EVENT = 'cabinet-demo-mode'
const SESSION_STORAGE_KEY = 'cabinet-customer-session'

type JsonRecord = Record<string, any>

interface ApiErrorShape {
  code?: string
  message?: string
  field_errors?: Record<string, string>
  request_id?: string
}

export class ApiError extends Error {
  code: string
  fieldErrors: Record<string, string>
  requestId?: string
  status: number

  constructor(payload: ApiErrorShape, status: number) {
    super(payload.message || `请求失败（${status}）`)
    this.name = 'ApiError'
    this.code = payload.code || `HTTP_${status}`
    this.fieldErrors = payload.field_errors || {}
    this.requestId = payload.request_id
    this.status = status
  }
}

const wait = (duration = 320) => new Promise((resolve) => window.setTimeout(resolve, duration))

function announceDemoMode() {
  window.dispatchEvent(new CustomEvent(DEMO_MODE_EVENT))
}

function getAccessToken() {
  try {
    const session = JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) || '{}') as {
      accessToken?: string
    }
    return session.accessToken || ''
  } catch {
    return ''
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  headers.set('Accept', 'application/json')
  if (!(init?.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  const accessToken = getAccessToken()
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)

  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers,
  })

  if (!response.ok) {
    let payload: ApiErrorShape = {}
    try {
      payload = await response.json()
    } catch {
      payload = {}
    }
    throw new ApiError(payload, response.status)
  }

  return response.json() as Promise<T>
}

async function withFallback<T>(live: () => Promise<T>, demo: () => Promise<T>): Promise<T> {
  try {
    return await live()
  } catch (error) {
    const isRecoverable = error instanceof TypeError
    if (!DEMO_FALLBACK || !isRecoverable) throw error
    announceDemoMode()
    await wait()
    return demo()
  }
}

function validateDimension(value: number, min: number, max: number, step: number) {
  return value >= min && value <= max && (value - min) % step === 0
}

function configurationBody(configuration: ProductConfiguration) {
  const partColors = Object.fromEntries(
    Object.entries(configuration.partColors || {})
      .map(([partId, colorCode]) => [partId.trim(), colorCode.trim()])
      .filter(([partId, colorCode]) => partId && colorCode),
  )
  return {
    product_version_id: configuration.productVersionId,
    width_mm: configuration.widthMm,
    height_mm: configuration.heightMm,
    depth_mm: configuration.depthMm,
    color_code: configuration.colorCode,
    part_colors: Object.keys(partColors).length ? partColors : undefined,
    hardware_code: configuration.hardwareCode,
    specification_id: configuration.specificationId || undefined,
    sku_code: configuration.skuCode || undefined,
  }
}

function mapSpecificationParts(value: unknown) {
  if (!Array.isArray(value)) return []
  return value
    .map((part: JsonRecord, partIndex: number) => ({
      id: String(part.id || '').trim(),
      name: String(part.name || '').trim(),
      allowedColorCodes: Array.isArray(part.allowed_color_codes)
        ? [...new Set(
            part.allowed_color_codes
              .map((code: unknown) => String(code || '').trim())
              .filter(Boolean),
          )]
        : [],
      defaultColorCode: String(part.default_color_code || '').trim(),
      required: part.required !== false,
      sortOrder: Number.isInteger(part.sort_order) ? Number(part.sort_order) : partIndex,
    }))
    .filter((part: { id: string; name: string }) => part.id && part.name)
    .sort(
      (left: { sortOrder: number }, right: { sortOrder: number }) =>
        left.sortOrder - right.sortOrder,
    )
}

function asJsonRecord(value: unknown): JsonRecord | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as JsonRecord
    : undefined
}

function mapPurchaseMode(value: unknown): PurchaseMode {
  return value === 'custom' ? 'custom' : 'standard'
}

function mapPurchaseModeExplicit(value: unknown): boolean {
  return value === true
}

function mapProductSpecification(
  value: unknown,
  fallbackSortOrder: number,
): ProductSpecification | undefined {
  const specification = asJsonRecord(value)
  if (!specification || specification.active === false) return undefined
  const id = String(specification.id || '').trim()
  const name = String(specification.name || '').trim()
  if (!id || !name) return undefined
  return {
    id,
    name,
    parts: mapSpecificationParts(specification.parts),
    sortOrder: Number.isInteger(specification.sort_order)
      ? Number(specification.sort_order)
      : fallbackSortOrder,
    purchaseMode: mapPurchaseMode(specification.purchase_mode),
    purchaseModeExplicit: mapPurchaseModeExplicit(
      specification.purchase_mode_explicit,
    ),
  }
}

function mapProductSpecifications(value: unknown): ProductSpecification[] {
  if (!Array.isArray(value)) return []
  const specificationsById = new Map<string, ProductSpecification>()
  value.forEach((item, index) => {
    const specification = mapProductSpecification(item, index)
    if (specification && !specificationsById.has(specification.id)) {
      specificationsById.set(specification.id, specification)
    }
  })
  return [...specificationsById.values()].sort(
    (left, right) => left.sortOrder - right.sortOrder,
  )
}

function skuEmbeddedSpecification(item: unknown, index: number): JsonRecord | undefined {
  const sku = asJsonRecord(item)
  if (!sku) return undefined
  const embedded = asJsonRecord(sku.specification) || {}
  return {
    id: embedded.id ?? sku.specification_id,
    name: embedded.name ?? sku.name ?? `规格 ${index + 1}`,
    active: embedded.active,
    sort_order: embedded.sort_order ?? index,
    parts: embedded.parts,
    purchase_mode: embedded.purchase_mode,
    purchase_mode_explicit: embedded.purchase_mode_explicit,
  }
}

export function mapProduct(payload: JsonRecord): Product {
  const version = payload.version || {}
  const rawBasePriceYuan = payload.basePriceYuan ?? payload.base_price_yuan
  const parsedBasePriceYuan = rawBasePriceYuan == null ? null : Number(rawBasePriceYuan)
  const configurableOptions = version.configurable_options || {}
  const colorsEnabled = configurableOptions.colors_enabled !== false
  const hardwareEnabled = configurableOptions.hardware_enabled !== false
  const defaultHardwareCode = String(version.default_hardware_code || '')
  const dimensions = version.dimensions || {}
  const media = Array.isArray(version.media) ? version.media : []
  const imageMedia = media.filter((item: JsonRecord) => item.type === 'image' && item.url)
  const rawHardwarePackages = hardwareEnabled && Array.isArray(version.hardware_packages)
    ? [...version.hardware_packages] as JsonRecord[]
    : []
  const hasExplicitDefaultHardware = rawHardwarePackages.some((hardware) =>
    Boolean(hardware.is_default) || String(hardware.code) === defaultHardwareCode
  )
  const skuItems = Array.isArray(version.skus)
    ? version.skus
    : Array.isArray(version.sku_catalog)
      ? version.sku_catalog
      : []
  const hasTopLevelSpecifications = Array.isArray(version.specifications)
  const topLevelSpecifications = mapProductSpecifications(version.specifications)
  const embeddedSpecifications = mapProductSpecifications(
    skuItems.map(skuEmbeddedSpecification),
  )
  const specifications = hasTopLevelSpecifications
    ? topLevelSpecifications
    : embeddedSpecifications
  const specificationsById = new Map(
    specifications.map((specification) => [specification.id, specification]),
  )
  const rawCustomizationProfile = asJsonRecord(version.customization_profile)
  const profileSpecificationId = String(
    rawCustomizationProfile?.specification_id
      || rawCustomizationProfile?.specificationId
      || '',
  ).trim()
  const embeddedProfileSpecification = mapProductSpecification(
    rawCustomizationProfile?.specification,
    0,
  )
  const profileSpecification = specificationsById.get(profileSpecificationId)
    || embeddedProfileSpecification
  const customizationProfile = rawCustomizationProfile?.schema_version === 1
    && profileSpecificationId
    && profileSpecification?.id === profileSpecificationId
    && profileSpecification.purchaseMode === 'custom'
    && profileSpecification.parts.length
    ? {
        schemaVersion: 1 as const,
        specificationId: profileSpecificationId,
        specification: profileSpecification,
      }
    : undefined
  const rawDimensionReferenceImage = asJsonRecord(dimensions.reference_image)
  const dimensionReferenceUrl = String(rawDimensionReferenceImage?.url || '').trim()
  const fallbackImage =
    payload.category === '衣柜'
      ? '/assets/product-wardrobe.avif'
      : payload.category === '餐边柜'
        ? '/assets/product-sideboard.avif'
        : '/assets/cabinet-hero.avif'
  const range = (value: JsonRecord | undefined, fallback = 0, fallbackLabel = '尺寸') => ({
    label: String(value?.label || '').trim() || fallbackLabel,
    min: Number(value?.min_mm ?? fallback),
    max: Number(value?.max_mm ?? fallback),
    step: Number(value?.step_mm ?? 1),
    default: Number(value?.default_mm ?? fallback),
  })

  return {
    id: String(payload.id),
    slug: String(payload.slug),
    name: String(version.name || payload.code || '未命名柜款'),
    sortOrder: Number(payload.sort_order ?? payload.display_order ?? Number.MAX_SAFE_INTEGER),
    basePriceYuan: parsedBasePriceYuan != null && Number.isFinite(parsedBasePriceYuan)
      ? parsedBasePriceYuan
      : null,
    category: String(payload.category || '柜类'),
    secondaryCategory: String(payload.secondary_category || ''),
    summary: String(version.description || '成熟柜款有限定制'),
    description: String(version.description || '成熟柜款有限定制'),
    version: String(version.version ?? ''),
    versionId: String(version.id || ''),
    image: String(imageMedia[0]?.url || fallbackImage),
    galleryImages: imageMedia.length
      ? imageMedia.slice(0, 5).map((item: JsonRecord, index: number) => ({
          id: String(item.id || `product-image-${index + 1}`),
          url: String(item.url),
          name: String(item.name || `商品图片 ${index + 1}`),
        }))
      : [{ id: 'fallback-image', url: fallbackImage, name: '商品展示图' }],
    colorsEnabled,
    hardwareEnabled,
    customSizeEnabled: dimensions.custom_enabled === true,
    colors: colorsEnabled && Array.isArray(version.colors)
      ? [...version.colors]
          .sort((left: JsonRecord, right: JsonRecord) =>
            String(left.code).localeCompare(String(right.code), 'zh-CN'),
          )
          .map((color: JsonRecord) => ({
            id: String(color.id),
            code: String(color.code),
            name: String(color.name),
            supplierCode: String(color.supplier_code || color.code),
            swatch: String(color.hex_value || '#88938e'),
            swatchImage: String(color.swatch_url || ''),
          }))
      : [],
    hardwarePackages: rawHardwarePackages.length
      ? rawHardwarePackages
          .map((hardware: JsonRecord, index: number) => {
            const isDefault = Boolean(hardware.is_default)
              || String(hardware.code) === defaultHardwareCode
              || (!hasExplicitDefaultHardware && index === 0)
            return {
              id: String(hardware.id),
              code: String(hardware.code),
              name: String(hardware.name),
              brand: String(hardware.brand || '合作品牌'),
              model: String(hardware.model || hardware.code),
              description: String(hardware.description || ''),
              level: (isDefault ? '标准' : '升级') as '标准' | '升级',
              isDefault,
              optionRole: (isDefault ? 'default' : 'upgrade') as 'default' | 'upgrade',
              specifications: Array.isArray(hardware.specifications)
                ? hardware.specifications.map((item: JsonRecord) => ({
                    componentKey: String(item.component_key || item.componentKey || '').trim(),
                    label: String(item.label || ''),
                    value: String(item.value || ''),
                    note: String(item.note || ''),
                  }))
                : [],
            }
          })
      : [],
    dimensionPresets: Array.isArray(dimensions.presets)
      ? dimensions.presets.map((preset: JsonRecord, index: number) => ({
          id: String(preset.id || `preset-${index + 1}`),
          label: String(preset.name || `常用尺寸 ${index + 1}`),
          widthMm: Number(preset.width_mm),
          heightMm: Number(preset.height_mm),
          depthMm: Number(preset.depth_mm),
          widthLabel: String(preset.width_label || '宽'),
          heightLabel: String(preset.height_label || '高'),
          depthLabel: String(preset.depth_label || '深'),
        }))
      : [],
    dimensionRanges: {
      widthMm: range(dimensions.width, 0, '宽度'),
      heightMm: range(dimensions.height, 0, '高度'),
      depthMm: range(dimensions.depth, 0, '深度'),
    },
    dimensionReferenceImage: dimensionReferenceUrl
      ? {
          url: dimensionReferenceUrl,
          name: String(rawDimensionReferenceImage?.name || '尺寸参考图').trim() || '尺寸参考图',
          alt: String(rawDimensionReferenceImage?.alt || rawDimensionReferenceImage?.name || '尺寸参考图').trim() || '尺寸参考图',
        }
      : undefined,
    customizationProfile,
    serviceNotes: ['最终尺寸以专业复尺和客户确认资料为准', '不支持异形与自由结构改造'],
    parameterGroups: Array.isArray(version.product_parameters)
      ? version.product_parameters.map((group: JsonRecord, groupIndex: number) => ({
          id: String(group.id || `parameter-group-${groupIndex + 1}`),
          name: String(group.name || '商品参数'),
          items: Array.isArray(group.items)
            ? group.items.map((item: JsonRecord, itemIndex: number) => ({
                id: String(item.id || `parameter-${groupIndex + 1}-${itemIndex + 1}`),
                label: String(item.label || ''),
                value: String(item.value || ''),
              }))
            : [],
        }))
      : [],
    specifications,
    skus: skuItems.map((item: JsonRecord, index: number) => {
      const dimensions = item.dimensions || {}
      const specification = asJsonRecord(item.specification) || {}
      const specificationId = String(specification.id || item.specification_id || '').trim()
      const catalogSpecification = specificationsById.get(specificationId)
      const inlineSpecificationParts = mapSpecificationParts(specification.parts)
      const specificationParts = inlineSpecificationParts.length
        ? inlineSpecificationParts
        : catalogSpecification?.parts || []
      const rawHardwareRequirements = Array.isArray(item.hardware_requirements)
        ? item.hardware_requirements
        : Array.isArray(item.hardwareRequirements)
          ? item.hardwareRequirements
          : []
      const hardwareRequirements = new Map<
        string,
        { componentKey: string; label: string; quantity: number }
      >()
      rawHardwareRequirements.forEach((requirement: JsonRecord) => {
        const componentKey = String(
          requirement.component_key || requirement.componentKey || '',
        ).trim()
        const quantity = Number(requirement.quantity)
        if (!componentKey || !Number.isSafeInteger(quantity) || quantity <= 0) return
        const normalizedKey = componentKey.toLocaleLowerCase('zh-CN')
        if (hardwareRequirements.has(normalizedKey)) return
        hardwareRequirements.set(normalizedKey, {
          componentKey,
          label: String(requirement.label || componentKey).trim() || componentKey,
          quantity,
        })
      })
      return {
        id: String(item.id || item.code || `sku-${index + 1}`),
        code: String(item.code || ''),
        name: String(item.name || `SKU ${index + 1}`),
        specificationId,
        specificationName: String(specification.name || item.name || `规格 ${index + 1}`),
        specificationParts,
        purchaseMode: specification.purchase_mode === undefined
          ? catalogSpecification?.purchaseMode || 'standard'
          : mapPurchaseMode(specification.purchase_mode),
        purchaseModeExplicit: specification.purchase_mode_explicit === undefined
          ? catalogSpecification?.purchaseModeExplicit === true
          : mapPurchaseModeExplicit(specification.purchase_mode_explicit),
        commonSizeId: String(item.size?.id || item.common_size_id || `size-${index + 1}`),
        sizeName: String(item.size?.name || item.name || `尺寸 ${index + 1}`),
        image: String(item.image || ''),
        imageEnabled: item.image_enabled === undefined
          ? Boolean(item.image)
          : Boolean(item.image_enabled),
        parameters: Array.isArray(item.parameters)
          ? item.parameters
              .map((parameter: JsonRecord, parameterIndex: number) => ({
                id: String(parameter.id || `sku-parameter-${index + 1}-${parameterIndex + 1}`),
                label: String(parameter.label || '').trim(),
                value: String(parameter.value || '').trim(),
              }))
              .filter((parameter: { label: string; value: string }) => parameter.label && parameter.value)
          : [],
        // Only map the public identity, label and count. Internal price/cost fields
        // that may coexist in a backend record must never enter customer state.
        hardwareRequirements: [...hardwareRequirements.values()],
        dimensions: {
          widthMm: Number(dimensions.width_mm || 0),
          heightMm: Number(dimensions.height_mm || 0),
          depthMm: Number(dimensions.depth_mm || 0),
          widthLabel: String(dimensions.width_label || '宽'),
          heightLabel: String(dimensions.height_label || '高'),
          depthLabel: String(dimensions.depth_label || '深'),
        },
      }
    }).filter((item: { code: string }) => item.code),
  }
}

function mapValidation(payload: JsonRecord): ConfigurationValidation {
  const errors = Array.isArray(payload.errors) ? payload.errors : []
  const warnings = Array.isArray(payload.warnings) ? payload.warnings : []
  const messages: ConfigurationValidation['messages'] = [
    ...errors.map((item: JsonRecord) => ({
      level: 'error' as const,
      field: item.field ? String(item.field) : undefined,
      message: String(item.message || item.code || '配置不符合规则'),
    })),
    ...warnings.map((item: JsonRecord) => ({
      level: 'warning' as const,
      message: String(item.message || item.code || '当前配置需要人工复核'),
    })),
  ]
  if (payload.valid && messages.length === 0) {
    messages.push({ level: 'info', message: '配置在当前产品的有限定制范围内。' })
  }
  return {
    valid: Boolean(payload.valid),
    requiresManualReview: Boolean(payload.requires_manual_review),
    messages,
  }
}

function formatDate(value?: string) {
  if (!value) return '等待更新'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('zh-CN', { hour12: false })
}

function statusMeta(status: string) {
  const values: Record<string, { stage: CustomerOrder['stage']; label: string; next: string }> = {
    submitted: {
      stage: 'inquiry',
      label: '待可接单审核',
      next: '工作人员正在核对产品定制边界和服务区域。',
    },
    accepted: {
      stage: 'survey_payment',
      label: '待预约量房',
      next: '询价已通过，可以补充地址并预约专业量房。',
    },
    frozen: {
      stage: 'freeze',
      label: '已生产冻结',
      next: '生产资料已经冻结，可以查看冻结编号和摘要。',
    },
    rejected: {
      stage: 'inquiry',
      label: '询价未通过',
      next: '当前需求超出有限定制范围，可联系客户顾问了解原因。',
    },
    closed: {
      stage: 'inquiry',
      label: '询价已关闭',
      next: '如需继续，请重新提交定制需求。',
    },
  }
  return values[status] || values.submitted
}

function imageForProduct(product: JsonRecord) {
  const slug = String(product?.slug || '')
  if (slug.includes('wardrobe')) return '/assets/product-wardrobe.avif'
  if (slug.includes('sideboard')) return '/assets/product-sideboard.avif'
  return '/assets/cabinet-hero.avif'
}

function mapInquirySummary(payload: JsonRecord): CustomerOrder {
  const meta = statusMeta(String(payload.status || 'submitted'))
  return {
    id: String(payload.id),
    orderNo: String(payload.number || payload.id),
    inquiryStatus: String(payload.status || 'submitted'),
    productName: String(payload.product?.name || '定制柜订单'),
    productImage: imageForProduct(payload.product),
    city: '服务地址待补充',
    stage: meta.stage,
    stageLabel: meta.label,
    updatedAt: formatDate(payload.updated_at),
    nextAction: meta.next,
    timeline: [
      {
        id: 'created',
        title: '询价已提交',
        description: '产品配置和联系方式已经进入系统。',
        occurredAt: formatDate(payload.created_at),
        state: payload.status === 'submitted' ? 'current' : 'done',
      },
      {
        id: 'review',
        title: payload.status === 'accepted' ? '询价已接受' : '可接单审核',
        description:
          payload.status === 'accepted'
            ? '配置在有限定制范围内，可以预约量房。'
            : '正在核对产品规则与服务区域。',
        state: payload.status === 'accepted' ? 'done' : 'upcoming',
      },
    ],
    survey: { status: 'pending', hasBooking: false },
    confirmation: { status: 'preparing', sections: [] },
    payments: [],
  }
}

function formatMoney(amountCents?: number) {
  if (typeof amountCents !== 'number') return '待确认'
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: 'CNY',
  }).format(amountCents / 100)
}

export function flattenConfirmationSections(rawSections: JsonRecord): CustomerOrder['confirmation']['sections'] {
  if (!rawSections || typeof rawSections !== 'object') return []
  const dimensions = rawSections.dimensions || {}
  const configuration = dimensions.configuration || {}
  const report = dimensions.survey_report || {}
  const design = rawSections.design || {}
  const material = rawSections.materials_hardware || {}
  const rules = rawSections.production_refund || {}
  const quote = rules.quote || {}
  const terms = rules.terms || {}
  const partColorItems = Array.isArray(material.part_colors)
    ? material.part_colors.map((part: JsonRecord) => ({
        label: String(part.part_name || '-'),
        value: `${part.color?.name || '-'} ${part.color?.supplier_code || ''}`.trim(),
      }))
    : []
  return [
    {
      id: 'dimensions',
      title: String(dimensions.title || '尺寸确认'),
      status: 'ready',
      items: [
        {
          label: '成品尺寸',
          value: `宽 ${configuration.width_mm ?? '-'} × 高 ${configuration.height_mm ?? '-'} × 深 ${configuration.depth_mm ?? '-'} 毫米`,
        },
        { label: '量房报告', value: `第 ${report.version ?? '-'} 版` },
        { label: '报告状态', value: String(report.status || '待确认') },
      ],
    },
    {
      id: 'design',
      title: String(design.title || '设计方案确认'),
      status: 'ready',
      items: [
        {
          label: '产品方案',
          value: String(design.product?.version?.name || '当前商品固定结构'),
        },
        {
          label: '设计文件',
          value: Array.isArray(design.documents)
            ? design.documents.map((item: JsonRecord) => item.name).filter(Boolean).join('、') || '待上传'
            : '待上传',
        },
      ],
    },
    {
      id: 'materials',
      title: String(material.title || '材质及五金确认'),
      status: 'ready',
      items: [
        ...(partColorItems.length
          ? partColorItems
          : [
              {
                label: '颜色',
                value: `${material.color?.name || '-'} ${material.color?.supplier_code || material.color?.code || ''}`.trim(),
              },
            ]),
        {
          label: '五金',
          value: `${material.hardware?.brand || ''} ${material.hardware?.name || '-'} ${material.hardware?.model || ''}`.trim(),
        },
      ],
    },
    {
      id: 'rules',
      title: String(rules.title || '生产与退款规则确认'),
      status: 'ready',
      items: [
        { label: '正式报价', value: formatMoney(quote.total_cents) },
        { label: '生产定金', value: formatMoney(quote.deposit_required_cents) },
        {
          label: '预计周期',
          value: terms.lead_time_days ? `${terms.lead_time_days} 天` : '以确认资料为准',
        },
      ],
    },
  ]
}

function mapLiveOrder(payload: JsonRecord): CustomerOrder {
  const order = mapInquirySummary(payload)
  const booking = payload.survey_booking
  const report = payload.survey_report
  const confirmationPackage = payload.confirmation_package
  const payments = Array.isArray(payload.payments) ? payload.payments : []

  if (booking) {
    order.city = [booking.address?.province, booking.address?.city, booking.address?.district]
      .filter(Boolean)
      .join('') || order.city
    order.stage = 'survey'
    order.stageLabel =
      booking.status === 'awaiting_payment'
        ? '待支付量房金'
        : booking.status === 'completed'
          ? '量房已完成'
          : '量房安排中'
    order.nextAction =
      booking.status === 'awaiting_payment'
        ? '请按公司收款信息支付量房服务费，并提交线下转账信息。'
        : '请关注量房安排和报告审核状态。'
    order.survey = {
      status: report ? 'completed' : booking.scheduled_at ? 'booked' : 'pending',
      hasBooking: true,
      bookingId: String(booking.id),
      appointment: booking.scheduled_at ? formatDate(booking.scheduled_at) : booking.requested_slot,
      surveyor: booking.surveyor_name,
      reportVersion: report ? `LF-${report.version}` : undefined,
    }
  }

  if (payload.quote) {
    order.stage = 'proposal'
    order.stageLabel = '方案报价已发送'
    order.nextAction = '请核对正式报价和即将发送的确认资料。'
  }
  if (confirmationPackage) {
    const waiting = confirmationPackage.status === 'awaiting_customer'
    order.stage = waiting ? 'confirmation' : 'deposit'
    order.stageLabel = waiting ? '待确认最终方案' : '待支付生产定金'
    order.nextAction = waiting
      ? '请逐项核对四部分资料，并使用短信验证码确认。'
      : '资料已确认，请按公司收款信息支付生产定金。'
    order.confirmation = {
      packageNo: String(confirmationPackage.id),
      status: waiting ? 'waiting' : 'confirmed',
      sections: flattenConfirmationSections(confirmationPackage.sections),
    }
  }
  if (payload.freeze) {
    order.stage = 'freeze'
    order.stageLabel = '已生产冻结'
    order.nextAction = '生产资料已经冻结，可以查看冻结编号和摘要。'
    order.freeze = {
      number: String(payload.freeze.number || payload.freeze.id || '-'),
      snapshotHash: String(payload.freeze.snapshot_hash || '-'),
      frozenAt: formatDate(payload.freeze.frozen_at),
    }
  }

  order.payments = payments.map((payment: JsonRecord) => ({
    id: String(payment.id),
    title: payment.kind === 'survey_fee' ? '量房服务费' : '生产定金',
    status:
      payment.status === 'paid'
        ? ('verified' as const)
        : payment.status === 'evidence_pending'
          ? ('verifying' as const)
          : ('waiting' as const),
    amountLabel:
      payment.status === 'paid'
        ? `${formatMoney(payment.amount_paid_cents)} 已核销`
        : formatMoney(payment.amount_due_cents),
    amountDueCents: Number(payment.amount_due_cents || 0),
  }))

  order.timeline = [
    {
      id: 'created',
      title: '询价已提交',
      description: '产品配置和联系方式已进入系统。',
      occurredAt: formatDate(payload.created_at),
      state: 'done',
    },
    {
      id: 'accepted',
      title: '可接单审核',
      description: ['accepted', 'frozen'].includes(String(payload.status))
        ? '审核已通过。'
        : '正在审核。',
      state: ['accepted', 'frozen'].includes(String(payload.status)) ? 'done' : 'current',
    },
    {
      id: 'survey',
      title: '专业量房',
      description: report ? '量房报告已形成。' : '等待预约、上门和报告审核。',
      state: report ? 'done' : booking ? 'current' : 'upcoming',
    },
    {
      id: 'confirmation',
      title: '方案与资料确认',
      description: confirmationPackage ? '确认资料包已生成。' : '量房审核后制作方案与报价。',
      state: confirmationPackage
        ? confirmationPackage.status === 'confirmed'
          ? 'done'
          : 'current'
        : 'upcoming',
    },
    {
      id: 'freeze',
      title: '生产冻结',
      description: payload.freeze ? '生产资料已经冻结。' : '定金核销和工艺复核后执行。',
      state: payload.freeze ? 'done' : 'upcoming',
    },
  ]
  return order
}

export const api = {
  demoModeEvent: DEMO_MODE_EVENT,

  getTrustCenter(): Promise<TrustCenter> {
    return withFallback(
      async () => {
        const result = await request<{ items: JsonRecord[] }>('/public/trust-center')
        const byKey = Object.fromEntries(result.items.map((item) => [item.key, item]))
        const company = byKey.company?.content || {}
        const guaranteeItems = Array.isArray(byKey.guarantee?.content?.items)
          ? byKey.guarantee.content.items
          : []
        const guaranteeTitles = ['未上门可申请退', '完整报价后付款', '确认后再生产', '平台统一受理']
        return {
          companyName: String(company.company_name || byKey.company?.title || '栖居木作'),
          companyIdentifier: String(company.description || '企业资料以上线审核内容为准'),
          officeAddress: String(company.office_address || '办公地址请向客户顾问核验'),
          factoryAddress: String(company.factory_address || '合作工厂信息请向客户顾问核验'),
          servicePhone: String(company.service_phone || company.complaint_phone || '400-000-0000'),
          complaintPhone: String(company.complaint_phone || '400-000-0000'),
          guarantees: guaranteeItems.map((description: unknown, index: number) => ({
            title: guaranteeTitles[index] || `保障说明 ${index + 1}`,
            description: String(description),
          })),
        }
      },
      async () => structuredClone(demoTrustCenter),
    )
  },

  checkServiceArea(province: string, city: string, district: string): Promise<ServiceAreaResult> {
    const params = new URLSearchParams({ province, city, district })
    return withFallback(
      async () => {
        const result = await request<JsonRecord>(
          `/public/service-areas/check?${params.toString()}`,
        )
        return {
          supported: Boolean(result.supported),
          serviceAreaId: result.service_area_id ? String(result.service_area_id) : undefined,
          areaName: [province, city, district].filter(Boolean).join(''),
          surveyRequired: Boolean(result.survey_required),
          surveyFeeCents:
            typeof result.survey_fee_cents === 'number' ? result.survey_fee_cents : undefined,
          serviceMessage: String(
            result.booking_notice ||
              result.message ||
              (result.supported ? '当前区域支持专业量房服务。' : '当前区域暂未开通服务。'),
          ),
          availableWindow: result.supported ? '具体上门时间由工作人员确认' : undefined,
        }
      },
      async () => {
        const unsupported = city.includes('拉萨') || city.includes('乌鲁木齐')
        return {
          supported: !unsupported,
          serviceAreaId: unsupported ? undefined : 'service-area-demo',
          areaName: [province, city, district].filter(Boolean).join(''),
          surveyRequired: !unsupported,
          serviceMessage: unsupported
            ? '当前区域暂未建立稳定的量房和安装服务，请先登记需求。'
            : '当前区域可提供专业量房服务，具体时间由工作人员确认。',
          availableWindow: unsupported ? undefined : '通常 2-4 个工作日可安排',
        }
      },
    )
  },

  listProducts(): Promise<Product[]> {
    return withFallback(
      async () => {
        const result = await request<{ items: JsonRecord[] }>('/catalog/products')
        return result.items.map(mapProduct)
      },
      async () => structuredClone(demoProducts),
    )
  },

  getProduct(slug: string): Promise<Product> {
    return withFallback(
      async () =>
        mapProduct(await request<JsonRecord>(`/catalog/products/${encodeURIComponent(slug)}`)),
      async () => {
        const product = demoProducts.find((item) => item.slug === slug)
        if (!product) throw new ApiError({ code: 'NOT_FOUND', message: '没有找到该产品' }, 404)
        return structuredClone(product)
      },
    )
  },

  validateConfiguration(
    configuration: ProductConfiguration,
  ): Promise<ConfigurationValidation> {
    return withFallback(
      async () =>
        mapValidation(
          await request('/configurations/validate', {
            method: 'POST',
            body: JSON.stringify(configurationBody(configuration)),
          }),
        ),
      async () => {
        const product = demoProducts.find((item) => item.id === configuration.productId)
        if (!product) {
          return {
            valid: false,
            requiresManualReview: false,
            messages: [{ level: 'error', message: '商品版本已失效，请重新选择。' }],
          }
        }

        const ranges = product.dimensionRanges
        const checks = [
          ['widthMm', ranges.widthMm.label || '宽度', configuration.widthMm, ranges.widthMm],
          ['heightMm', ranges.heightMm.label || '高度', configuration.heightMm, ranges.heightMm],
          ['depthMm', ranges.depthMm.label || '深度', configuration.depthMm, ranges.depthMm],
        ] as const
        const messages: ConfigurationValidation['messages'] = []

        checks.forEach(([field, label, value, range]) => {
          if (!validateDimension(value, range.min, range.max, range.step)) {
            messages.push({
              level: 'error',
              field,
              message: `${label}需在 ${range.min}-${range.max} 毫米内，并按 ${range.step} 毫米调整。`,
            })
          }
        })

        if (
          product.colors.length
          && !product.customizationProfile
          && !product.colors.some((item) => item.code === configuration.colorCode)
        ) {
          messages.push({ level: 'error', field: 'colorCode', message: '所选颜色已不可用。' })
        }

        const profileParts = product.customizationProfile?.specification.parts || []
        if (product.customizationProfile) {
          if (configuration.specificationId !== product.customizationProfile.specificationId) {
            messages.push({
              level: 'error',
              field: 'specificationId',
              message: '当前定制配置已变更，请重新选择。',
            })
          }
          const knownPartIds = new Set(profileParts.map((part) => part.id))
          Object.keys(configuration.partColors || {}).forEach((partId) => {
            if (!knownPartIds.has(partId)) {
              messages.push({
                level: 'error',
                field: `partColors.${partId}`,
                message: '提交了当前方案中不存在的选色项。',
              })
            }
          })
          profileParts.forEach((part) => {
            const selected = String(configuration.partColors?.[part.id] || '')
            if (!selected && part.required) {
              messages.push({
                level: 'error',
                field: `partColors.${part.id}`,
                message: `请选择${part.name}。`,
              })
              return
            }
            if (!selected) return
            const allowed = part.allowedColorCodes.length
              ? part.allowedColorCodes
              : product.colors.map((color) => color.code)
            if (!allowed.includes(selected)) {
              messages.push({
                level: 'error',
                field: `partColors.${part.id}`,
                message: `${part.name}所选颜色已不可用。`,
              })
            }
          })
        }
        if (
          product.hardwarePackages.length
          && !product.hardwarePackages.some((item) => item.code === configuration.hardwareCode)
        ) {
          messages.push({
            level: 'error',
            field: 'hardwareCode',
            message: '所选五金方案已不可用。',
          })
        }

        const manualReview =
          configuration.heightMm > 2400 ||
          (product.category === '鞋柜' && configuration.depthMm < 350)
        if (manualReview) {
          messages.push({
            level: 'warning',
            message: '当前配置需要专业复尺和工艺人员进一步审核。',
          })
        }
        if (messages.length === 0) {
          messages.push({ level: 'info', message: '配置在当前产品的有限定制范围内。' })
        }

        return {
          valid: !messages.some((item) => item.level === 'error'),
          requiresManualReview: manualReview,
          messages,
        }
      },
    )
  },

  sendSms(phone: string): Promise<{ retryAfterSeconds: number; demoCode?: string }> {
    return withFallback(
      async () => {
        const result = await request<JsonRecord>('/customer-auth/sms/send', {
          method: 'POST',
          body: JSON.stringify({ phone, purpose: 'login' }),
        })
        return {
          retryAfterSeconds: Number(result.retry_after_seconds || 60),
          demoCode: result.dev_code ? String(result.dev_code) : undefined,
        }
      },
      async () => ({ retryAfterSeconds: 60, demoCode: '246810' }),
    )
  },

  verifySms(
    phone: string,
    code: string,
  ): Promise<{ customerId: string; phone: string; accessToken: string }> {
    return withFallback(
      async () => {
        const result = await request<JsonRecord>('/customer-auth/sms/verify', {
          method: 'POST',
          body: JSON.stringify({ phone, code, purpose: 'login' }),
        })
        return {
          customerId: String(result.customer?.id),
          phone: String(result.customer?.phone || phone),
          accessToken: String(result.access_token),
        }
      },
      async () => {
        if (code !== '246810') {
          throw new ApiError({ code: 'SMS_CODE_INVALID', message: '演示验证码不正确' }, 422)
        }
        return { customerId: 'customer-demo', phone, accessToken: 'demo-access-token' }
      },
    )
  },

  createInquiry(payload: InquiryPayload): Promise<InquiryResult> {
    return withFallback(
      async () => {
        const result = await request<JsonRecord>('/inquiries', {
          method: 'POST',
          headers: { 'Idempotency-Key': crypto.randomUUID() },
          body: JSON.stringify({
            ...configurationBody(payload.configuration),
            service_area_id: payload.serviceAreaId || undefined,
            contact_name: payload.contactName,
            notes: payload.notes || '',
          }),
        })
        return {
          inquiryId: String(result.id),
          inquiryNo: String(result.number),
        }
      },
      async () => ({
        inquiryId: crypto.randomUUID(),
        inquiryNo: `XJ${new Date().toISOString().slice(0, 10).replaceAll('-', '')}0001`,
      }),
    )
  },

  getMyOrders(): Promise<CustomerOrder[]> {
    return withFallback(
      async () => {
        const result = await request<{ items: JsonRecord[] }>('/me/inquiries')
        return result.items.map(mapInquirySummary)
      },
      async () => structuredClone(demoOrders),
    )
  },

  getOrder(id: string): Promise<CustomerOrder> {
    return withFallback(
      async () =>
        mapLiveOrder(await request<JsonRecord>(`/me/orders/${encodeURIComponent(id)}`)),
      async () => {
        const order = demoOrders.find((item) => item.id === id)
        if (!order) throw new ApiError({ code: 'NOT_FOUND', message: '没有找到该订单' }, 404)
        return structuredClone(order)
      },
    )
  },

  createSurveyBooking(
    orderId: string,
    payload: SurveyBookingPayload,
  ): Promise<{ bookingId: string; status: string }> {
    return withFallback(
      async () => {
        const result = await request<JsonRecord>(
          `/orders/${encodeURIComponent(orderId)}/survey-bookings`,
          {
            method: 'POST',
            headers: { 'Idempotency-Key': crypto.randomUUID() },
            body: JSON.stringify({
              address: {
                province: payload.province,
                city: payload.city,
                district: payload.district,
                detail: payload.detail,
              },
              requested_slot: payload.requestedSlot || null,
            }),
          },
        )
        return {
          bookingId: String(result.id),
          status: String(result.status),
        }
      },
      async () => ({ bookingId: 'survey-booking-demo', status: 'awaiting_payment' }),
    )
  },

  uploadPaymentEvidence(
    orderId: string,
    details: {
      milestoneId: string
      payerName: string
      bankReference: string
      amountCents: number
      attachmentName?: string
    },
  ): Promise<{ status: 'verifying' }> {
    return withFallback(
      async () => {
        await request(`/orders/${encodeURIComponent(orderId)}/payment-evidence`, {
          method: 'POST',
          body: JSON.stringify({
            milestone_id: details.milestoneId,
            payer_name: details.payerName,
            bank_reference: details.bankReference,
            amount_cents: details.amountCents,
            attachment_url: details.attachmentName || null,
          }),
        })
        return { status: 'verifying' as const }
      },
      async () => ({ status: 'verifying' as const }),
    )
  },

  sendConfirmationSms(
    confirmationId: string,
  ): Promise<{ retryAfterSeconds: number; demoCode?: string }> {
    return withFallback(
      async () => {
        const result = await request<JsonRecord>(
          `/confirmations/${encodeURIComponent(confirmationId)}/sms/send`,
          { method: 'POST', body: JSON.stringify({}) },
        )
        return {
          retryAfterSeconds: Number(result.retry_after_seconds || 60),
          demoCode: result.dev_code ? String(result.dev_code) : undefined,
        }
      },
      async () => ({ retryAfterSeconds: 60, demoCode: '246810' }),
    )
  },

  confirmPackage(
    confirmationId: string,
    code: string,
  ): Promise<{ confirmedAt: string }> {
    return withFallback(
      () =>
        request<JsonRecord>(`/confirmations/${encodeURIComponent(confirmationId)}/confirm`, {
          method: 'POST',
          headers: { 'Idempotency-Key': crypto.randomUUID() },
          body: JSON.stringify({ code }),
        }).then((result: JsonRecord) => ({ confirmedAt: String(result.confirmed_at) })),
      async () => {
        if (code !== '246810') {
          throw new ApiError({ code: 'SMS_CODE_INVALID', message: '演示验证码不正确' }, 422)
        }
        return { confirmedAt: new Date().toISOString() }
      },
    )
  },
}
